import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { BriefcaseIcon, TrendingUpIcon, BarChartIcon, TargetIcon, ShieldCheckIcon } from 'lucide-react';

// Vier onderscheiden profielen. Eerder stonden hier twee kaarten die
// allebei over het opdrogende netwerk gingen, dat is nu één kaart.
const profiles = [{
  icon: TrendingUpIcon,
  title: 'Je netwerk loopt tegen zijn grens aan',
  desc: 'Doorverwijzingen brachten je tot hier, maar de groei die je nu wilt past niet meer in wat je netwerk kan leveren. Je zoekt een kanaal dat los staat van wie je toevallig kent.'
}, {
  icon: BarChartIcon,
  title: 'Je klantwaarde is hoog genoeg om te investeren',
  desc: 'Eén extra opdracht per maand verdient het traject terug. Bij die orderwaarde is meten vooraf goedkoper dan achteraf ontdekken dat het budget de verkeerde kant op ging.'
}, {
  icon: TargetIcon,
  title: 'Je hebt eerder geadverteerd zonder zicht op resultaat',
  desc: 'Er ging budget om, er kwam verkeer, maar niemand kon zeggen welke zoekopdracht een klant opleverde. Je wilt die vraag deze keer wel beantwoord hebben.'
}, {
  icon: BriefcaseIcon,
  title: 'Je stuurt liever op cijfers dan op aannames',
  desc: 'Je bent gewend beslissingen te onderbouwen. Een marketingvoorstel op basis van een zoekwoordentool voldoet niet aan de standaard die je in de rest van je bedrijf hanteert.'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: [0.23, 1, 0.32, 1]
    }
  }
};
export function ForWho() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="voor-wie" ref={ref} className="py-20 md:py-24 relative" style={{
    background: '#F8FAFC'
  }} data-id="element-172" data-mp="3n0hy02nvm0dy">
      <div className="max-w-[1200px] mx-auto px-6" data-id="element-173" data-mp="3ogvpmu46t5hf">
        <motion.div className="max-w-3xl mb-14" initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} data-id="element-174" data-mp="3tggsvgqe3zzy">
          <div className="section-label" data-id="element-175" data-mp="3ajfljp9d5nsu">Voor wie</div>
          <h2 className="text-[clamp(2rem,3.6vw,2.8rem)] font-bold text-brand-dark leading-tight tracking-tight mb-4" data-id="element-176" data-mp="3ak8muapjzl3l">
            CorePrint past bij bedrijven die willen weten waar hun budget heen
            gaat
          </h2>
          <p className="text-brand-gray text-lg leading-relaxed" data-id="element-177" data-mp="2mtl9h9wvc39n">
            Het traject vraagt een investering vooraf en negentig dagen geduld.
            Dat verdient zich terug bij bedrijven waar een klant genoeg waard is
            om die tijd voor te nemen.
          </p>
        </motion.div>

        <motion.div className="grid md:grid-cols-2 gap-6 mb-6 items-stretch" variants={containerVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'} data-id="element-178" data-mp="237t4jmiq5hur">
          {profiles.map(p => <motion.div key={p.title} className="card flex flex-col" variants={itemVariants} data-id="element-179" data-mp="2d45y8ke9rvpi">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{
            background: '#EFF6FF',
            border: '1px solid rgba(0, 82, 204, 0.15)'
          }} data-id="element-180" data-mp="2m6oxkuiujq70">
                <p.icon size={22} className="text-brand-primary" data-id="element-181" data-mp="2vybgze5c8vku" />
              </div>
              <h3 className="font-bold text-brand-dark text-lg mb-3" data-id="element-182" data-mp="39w9j9l6dx1jz">
                {p.title}
              </h3>
              <p className="text-brand-gray text-[0.96rem] leading-relaxed" data-id="element-183" data-mp="34utzrxjuv33p">
                {p.desc}
              </p>
            </motion.div>)}
        </motion.div>

        {/* Expertise */}
        <motion.div className="rounded-2xl p-8 border border-[#E8EDF8] bg-white mb-6" initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.25
      }} data-id="element-184" data-mp="184hgww4zbm21">
          <div className="flex items-start gap-4" data-id="element-185" data-mp="t91t38uzsme3">
            <div className="w-12 h-12 rounded-xl flex-shrink-0 flex items-center justify-center" style={{
            background: '#EFF6FF',
            border: '1px solid rgba(0, 82, 204, 0.15)'
          }} data-id="element-186" data-mp="t88mq8zy3ywh">
              <BriefcaseIcon size={22} className="text-brand-primary" data-id="element-187" data-mp="29ms1grt59wtj" />
            </div>
            <div data-id="element-188" data-mp="2js00fwj1bj6q">
              <div className="font-bold text-brand-dark text-lg mb-2" data-id="element-189" data-mp="2jqf5i5st5y8c">
                Wie er aan tafel zit
              </div>
              <p className="text-brand-gray text-[0.96rem] leading-relaxed" data-id="element-190" data-mp="2euqcymf7hhlj">
                Een traject wordt uitgevoerd door specialisten op het gebied van{' '}
                <strong className="text-brand-dark font-semibold" data-id="element-191" data-mp="25za8zckqlm28">
                  adverteren, design, SEO, data en development
                </strong>
                . Je hebt gedurende het hele traject een vast aanspreekpunt en je
                spreekt de mensen die het werk doen.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Marktexclusiviteit, uitsluitend bij Platinum. Eerder stond hier
            een schaarste-banner die suggereerde dat exclusiviteit voor
            iedereen gold. Dat klopt niet en dat wekt verkeerde verwachtingen. */}
        <motion.div className="rounded-2xl p-8 md:p-10 border border-[#E8EDF8] bg-white" initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.32
      }} data-id="element-192" data-mp="3hgjue4nfeo66">
          <div className="flex flex-col md:flex-row md:items-center gap-8" data-id="element-193" data-mp="19u07pt3swqyx">
            <div className="flex-grow" data-id="element-194" data-mp="1xkmelh22k57c">
              <div className="flex items-center gap-3 mb-3" data-id="element-195" data-mp="1xhi6i8lhgpbe">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{
                background: '#EFF6FF',
                border: '1px solid rgba(0, 82, 204, 0.15)'
              }} data-id="element-196" data-mp="22hw22r5we6sj">
                  <ShieldCheckIcon size={20} className="text-brand-primary" data-id="element-197" data-mp="d2ol0awo3wyf" />
                </div>
                <span className="text-xs font-bold uppercase tracking-wide text-brand-primary" data-id="element-198" data-mp="i3c6yi61hnls">
                  Alleen bij Platinum
                </span>
              </div>
              <h3 className="font-bold text-brand-dark text-xl md:text-2xl mb-3" data-id="element-199" data-mp="5kqco9q82rws">
                Marktexclusiviteit
              </h3>
              <p className="text-brand-gray leading-relaxed max-w-2xl" data-id="element-200" data-mp="jj7zm58b00dn">
                Bij het Platinum-pakket werken we in jouw markt en regio niet
                ook voor je directe concurrent, zolang het traject loopt. Bij
                Brons, Zilver en Goud geldt die afspraak niet. In het
                kennismakingsgesprek bespreken we of jouw sector en regio
                beschikbaar zijn.
              </p>
            </div>
            <a href="#gesprek" className="inline-flex items-center justify-center bg-brand-primary text-white font-semibold px-7 py-4 rounded-lg hover:opacity-90 transition-opacity duration-200 flex-shrink-0" data-id="element-201" data-mp="3rzta5qgl31iv">
              Plan een gesprek
            </a>
          </div>
        </motion.div>
      </div>
    </section>;
}